import React from 'react';
import { PlainRoute, Route } from 'react-router';
import TrackPurchase from '@reverbdotcom/commons/src/components/track_purchase';
import isNode from '@reverbdotcom/commons/src/is-node';

import {
  MParticlePageName,
} from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { events } from '@reverbdotcom/commons/src/elog';
import { Experiment } from '@reverbdotcom/commons';
import { buildCheckoutPageView, fetchCartItems } from '../components/shared/checkout_events';
import TrackCartView from '../components/cart/track_cart_view';

const CART_URI = 'cart';
const CHECKOUT_CONFIRMATION_URI = 'multi-checkout/:order_bundle_id/confirmation';
const CHECKOUT_SHIPPING_URI = 'multi-checkout/:checkout_id/:payment_method';
const CHECKOUT_PAYMENT_URI = 'multi-checkout/:checkout_id/:payment_method/payment';
const CHECKOUT_REVIEW_URI = 'multi-checkout/:checkout_id/:payment_method/review';
const CHECKOUT_CHANGE_SHIPPING_URI = 'multi-checkout/:checkout_id/:payment_method/change_shipping';
const CHECKOUT_CHANGE_PAYMENT_URI = 'multi-checkout/:checkout_id/:payment_method/change_payment';
const CHECKOUT_ORDER_BUNDLE_CONFIRMATION_URI = 'multi-checkout/:checkout_id/:payment_method/:order_bundle_id/confirmation';
// we mount the router globally on bundled checkout pages. This catch-all route prevents 404 errors in RRE
// for routes that we don't need to track.
const CHECKOUT_NO_OP_URI = 'multi-checkout/*';
const CART_NO_OP_URI = 'cart/*';

const VALID_URI_PAYMENT_METHODS = Object.freeze([
  'direct',
  'paypal',
  'affirm',
  'partial_payments',
  'vaulted_paypal',
  'sofort',
  'apple_pay',
]);

const PAYMENT_METHOD_FROM_URI = {
  direct: 'direct_checkout',
  paypal: 'paypal',
  affirm: 'affirm',
  vaulted_paypal: 'paypal',
  sofort: 'sofort',
  apple_pay: 'apple_pay',
};

const PAGE_NAMES: Record<string, MParticlePageName> = Object.freeze({
  [CART_URI]: MParticlePageName.Cart,
  [CHECKOUT_SHIPPING_URI]: MParticlePageName.Shipping,
  [CHECKOUT_PAYMENT_URI]: MParticlePageName.Payment,
  [CHECKOUT_REVIEW_URI]: MParticlePageName.ReviewOrder,
  [CHECKOUT_CHANGE_SHIPPING_URI]: MParticlePageName.ChangeShipping,
  [CHECKOUT_CHANGE_PAYMENT_URI]: MParticlePageName.ChangePayment,
});

interface CheckoutParams {
  checkout_id?: string;
  payment_method?: string;
}

export async function checkoutPageViewAttributes({ params, innerRoute, userContext }:
{ params: CheckoutParams; innerRoute: PlainRoute, userContext: Partial<events.UserContext> },
) {
  if (!isTrackableRoute(params) || isNode) {
    return {};
  }

  const pageName = PAGE_NAMES[innerRoute.path];
  if (!pageName) {
    return {};
  }

  const paymentMethod = PAYMENT_METHOD_FROM_URI[params.payment_method] || '';

  const user = {
    // buildCheckoutPageView only looks at the 'experiments' field so we build up a Partial<IUser> using the
    // experiments from the mostly-incompatible events.UserContext. The experiment types are the same between
    // the two but we still need to cast due to incompatible required/optional field modifiers :-(
    experiments: (userContext.experiments as Experiment[]),
  };

  const cartItems = await fetchCartItems();

  return buildCheckoutPageView({
    pageName,
    paymentMethod,
    cartItems,
    user,
  });
}

function isTrackableRoute(params) {
  const paymentMethod = params.payment_method;
  return !paymentMethod || VALID_URI_PAYMENT_METHODS.indexOf(paymentMethod) > -1;
}

const CHECKOUT_URIS = Object.freeze([
  CHECKOUT_SHIPPING_URI,
  CHECKOUT_PAYMENT_URI,
  CHECKOUT_REVIEW_URI,
  CHECKOUT_CHANGE_SHIPPING_URI,
  CHECKOUT_CHANGE_PAYMENT_URI,
]);

const CHECKOUT_ROUTES = CHECKOUT_URIS.map((route) => {
  return (
    <Route
      key={route}
      path={route}
      props={{
        pageViewEvent: (state) => checkoutPageViewAttributes(state),
      }}
    />
  );
});

const ALL_CHECKOUT_ROUTES = [
  <Route
    key={CART_URI}
    path={CART_URI}
    getComponent={(_location, cb) => {
      cb(null, TrackCartView);
    }}
  />,
  <Route
    key={CHECKOUT_CONFIRMATION_URI} // also matches view shipping route so needs to come first
    path={CHECKOUT_CONFIRMATION_URI}
    getComponent={(_location, cb) => {
      cb(null, TrackPurchase);
    }}
  />,
  <Route
    key={CHECKOUT_ORDER_BUNDLE_CONFIRMATION_URI} // also matches no-op route so needs to come first
    path={CHECKOUT_ORDER_BUNDLE_CONFIRMATION_URI}
    getComponent={(_location, cb) => {
      cb(null, TrackPurchase);
    }}
  />,
  ...CHECKOUT_ROUTES,
  <Route
    key={CHECKOUT_NO_OP_URI}
    path={CHECKOUT_NO_OP_URI}
  />,
  <Route
    key={CART_NO_OP_URI}
    path={CART_NO_OP_URI}
  />,
];

export default ALL_CHECKOUT_ROUTES;
